import { Box, Container, Typography, Button } from '@mui/material';
import { motion } from 'framer-motion';
import EmailIcon from '@mui/icons-material/Email';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import SignalBackground from './SignalBackground';

const Hero = () => {
  return (
    <Box
      id="hero"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 50% 50%, rgba(0, 216, 255, 0.15) 0%, rgba(10, 25, 47, 0) 50%)',
          zIndex: 0,
        },
      }}
    >
      <SignalBackground />
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', md: 'row' }, alignItems: 'center', gap: { xs: 4, md: 8 } }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', md: 'flex-start' }, textAlign: { xs: 'center', md: 'left' } }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: '#00D8FF',
                  fontFamily: '"Fira Code", monospace',
                  mb: 2,
                }}
              >
                Hi, my name is
              </Typography>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', md: '4rem' },
                  fontWeight: 'bold',
                  background: 'linear-gradient(45deg, #00D8FF, #64FFDA)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 2,
                }}
              >
                Ahmad Jamal Khan
              </Typography>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '1.5rem', md: '2.5rem' },
                  color: 'rgba(230, 241, 255, 0.8)',
                  mb: 3,
                }}
              >
                I build RF, SDR & test systems.
              </Typography>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(230, 241, 255, 0.6)',
                  maxWidth: '600px',
                  mb: 4,
                  fontSize: '1.1rem',
                  lineHeight: 1.7,
                }}
              >
                I'm an RF / DSP engineer specializing in software-defined radio, signal processing and LabVIEW test automation. I reverse-engineer drone RF protocols, build multi-node SDR and GNSS testbeds, and develop automated RF measurement systems — currently as an RF Systems Engineer at Shaheen Aero Traders.
              </Typography>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}
            >
              <Button
                variant="outlined"
                startIcon={<EmailIcon />}
                href="mailto:engr.ahmadjamalkhan@gmail.com"
                sx={{
                  borderColor: '#00D8FF',
                  color: '#00D8FF',
                  '&:hover': {
                    borderColor: '#00D8FF',
                    backgroundColor: 'rgba(0, 216, 255, 0.1)',
                  },
                }}
              >
                Get In Touch
              </Button>
              <Button
                variant="outlined"
                startIcon={<GitHubIcon />}
                href="https://github.com/Skeletoskull"
                target="_blank"
                sx={{
                  borderColor: '#00D8FF',
                  color: '#00D8FF',
                  '&:hover': {
                    borderColor: '#00D8FF',
                    backgroundColor: 'rgba(0, 216, 255, 0.1)',
                  },
                }}
              >
                GitHub
              </Button>
              <Button
                variant="outlined"
                startIcon={<LinkedInIcon />}
                href="https://www.linkedin.com/in/ahmad-jamal-khan-jadoon/"
                target="_blank"
                sx={{
                  borderColor: '#00D8FF',
                  color: '#00D8FF',
                  '&:hover': {
                    borderColor: '#00D8FF',
                    backgroundColor: 'rgba(0, 216, 255, 0.1)',
                  },
                }}
              >
                LinkedIn
              </Button>
            </motion.div>
          </Box>

          <Box
            sx={{
              flex: '0 0 auto',
              display: 'block',
              width: { xs: 200, sm: 240, md: 300 },
              height: { xs: 200, sm: 240, md: 300 },
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{
                duration: 0.5,
                delay: 0.3,
                type: "spring",
                stiffness: 100
              }}
              style={{
                width: '100%',
                height: '100%',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '10px',
                    left: '10px',
                    right: '-10px',
                    bottom: '-10px',
                    border: '2px solid #00D8FF',
                    borderRadius: '8px',
                    zIndex: 0,
                  },
                }}
              >
                <Box
                  component="img"
                  src="profile.jpg"
                  alt="Ahmad Jamal Khan"
                  loading="eager"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center top',
                    borderRadius: '8px',
                    position: 'relative',
                    zIndex: 1,
                    transition: 'all 0.3s ease',
                    filter: 'grayscale(15%) contrast(1.02)',
                    '&:hover': {
                      transform: 'translate(-5px, -5px)',
                      filter: 'grayscale(0%)',
                    },
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    console.error('Error loading image:', target.src);
                    target.onerror = null;
                  }}
                />
              </Box>
            </motion.div>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Hero; 